import { JSX, useEffect, useId, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  matchRoutes,
  RouteObject,
  useLocation,
  useParams,
  useResolvedPath,
  useRoutes,
} from 'react-router-dom';
import {
  Accordion,
  AccordionPanel,
  AccordionTrigger,
  Button,
  Link,
} from '@backstage/ui';
import { RiArrowDownSLine } from '@remixicon/react';

import styles from './SidebarNav.module.css';

/** A page of a `SidebarNav`. */
export type SubRoute = {
  /**
   * Path of the page relative to the route that renders the `SidebarNav`,
   * e.g. `my-api`. A leading `/` and a trailing `/*` are ignored.
   */
  path: string;
  /** Label in the sidebar and title of the browser tab. */
  title: string;
  /** Content shown while the page is selected. */
  children: JSX.Element;
};

/** A titled group of pages that the user can collapse. */
export type SubRouteHeader = {
  title: string;
  subroutes: SubRoute[];
};

export interface SidebarNavProps {
  /** Pages and groups of pages, in display order. */
  routes: Array<SubRoute | SubRouteHeader>;
  /**
   * Accessible name of the navigation landmark.
   * @defaultValue 'Sections'
   */
  label?: string;
  className?: string;
}

const isHeader = (item: SubRoute | SubRouteHeader): item is SubRouteHeader =>
  'subroutes' in item;

const normalizePath = (path: string) =>
  path.replace(/\/\*$/, '').replace(/^\//, '');

function useSelectedPage(pages: SubRoute[]) {
  const params = useParams();
  const { pathname } = useLocation();
  const routePath = useResolvedPath('.').pathname;

  if (!pages.length) {
    throw new Error('No routes provided to SidebarNav.');
  }

  const routes: RouteObject[] = pages.map(page => ({
    caseSensitive: false,
    path: `${normalizePath(page.path)}/*`,
    element: page.children,
  }));
  // Without a page in the URL the first page is shown
  const fallback: RouteObject = { path: '*', element: pages[0].children };

  const element = useRoutes([...routes, fallback]);

  // The pages are relative to the route that renders the SidebarNav. Inside
  // a splat route (an entity tab) the splat holds the selected page;
  // without one the route path is the base.
  const splat = params['*'];
  const basePath = (
    splat === undefined
      ? routePath
      : pathname.slice(0, pathname.length - splat.length)
  ).replace(/\/$/, '');

  const [match] = matchRoutes(routes, pathname.slice(basePath.length)) ?? [];
  const index = routes.findIndex(route => route.path === match?.route.path);

  return { page: pages[index] ?? pages[0], element, basePath };
}

type SidebarNavLinkProps = {
  page: SubRoute;
  basePath: string;
  isCurrent: boolean;
};

const SidebarNavLink = ({ page, basePath, isCurrent }: SidebarNavLinkProps) => (
  <li>
    <Link
      href={`${basePath}/${normalizePath(page.path)}`}
      aria-current={isCurrent ? 'page' : undefined}
      className={styles.link}
    >
      {page.title}
    </Link>
  </li>
);

/**
 * Two-column page with a vertical list of routed pages on the left and the
 * selected page on the right. The pages are routes relative to where the
 * component is mounted, so every page has its own URL. Groups
 * (`SubRouteHeader`) render as collapsible sections. Below 768 px the list
 * collapses into a menu button above the content.
 *
 * @example
 * <SidebarNav
 *   routes={[
 *     { path: 'overview', title: 'Overview', children: <Overview /> },
 *     {
 *       title: 'Production',
 *       subroutes: [{ path: 'v2', title: 'v2.0', children: <Notes /> }],
 *     },
 *   ]}
 * />
 */
export const SidebarNav = ({
  routes,
  label = 'Sections',
  className,
}: SidebarNavProps) => {
  const pages = routes.flatMap(item =>
    isHeader(item) ? item.subroutes : item,
  );
  const { page, element, basePath } = useSelectedPage(pages);
  const [menuOpen, setMenuOpen] = useState(false);
  const listId = useId();

  // The narrow-screen menu closes once a page is chosen
  useEffect(() => setMenuOpen(false), [page]);

  const renderLink = (item: SubRoute) => (
    <SidebarNavLink
      key={item.path}
      page={item}
      basePath={basePath}
      isCurrent={item === page}
    />
  );

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      <nav
        aria-label={label}
        className={styles.nav}
        data-menu-open={menuOpen || undefined}
      >
        <Button
          variant="secondary"
          className={styles.menuButton}
          aria-expanded={menuOpen}
          aria-controls={listId}
          iconEnd={<RiArrowDownSLine aria-hidden />}
          onPress={() => setMenuOpen(open => !open)}
        >
          {page.title}
        </Button>
        <ul id={listId} className={styles.list}>
          {routes.map(item =>
            isHeader(item) ? (
              <li key={`group-${item.title}`}>
                <Accordion defaultExpanded className={styles.group}>
                  <AccordionTrigger
                    title={item.title}
                    className={styles.groupTrigger}
                  />
                  <AccordionPanel>
                    <ul className={styles.list}>
                      {item.subroutes.map(renderLink)}
                    </ul>
                  </AccordionPanel>
                </Accordion>
              </li>
            ) : (
              renderLink(item)
            ),
          )}
        </ul>
      </nav>
      <div className={styles.content}>
        <Helmet title={page.title} />
        {element}
      </div>
    </div>
  );
};
